import { useEffect, useMemo } from 'react'

import { DateTime } from 'luxon'
import { Layer, Popup, Source, useMap } from 'react-map-gl/mapbox'

import { useDashboard } from '@/hooks'

import { useTrackerMapContext } from './TrackerMapContext'
import { formatTemp, getRowStatus, rowId, statusToMarkerImage, tsToMillis } from './utils'

// History marker colors: below the low boundary = blue (too cold), above the high
// boundary = red (too hot), in range = green, no reading = gray.
const HISTORY_BLUE = '#3b82f6'
const HISTORY_RED = '#ef4444'
const HISTORY_GREEN = '#22c55e'
const HISTORY_GRAY = '#9ca3af'

// Data-driven circle color: grey for fixes with no reading, blue below the low
// boundary, red at/above the high boundary, else green. Either boundary may be
// null (that end isn't defined) and is simply omitted from the expression.
const historyColorExpression = (lowC: number | null, highC: number | null): mapboxgl.ExpressionSpecification => {
  const cases: unknown[] = ['case', ['!', ['to-boolean', ['get', 'hasTemp']]], HISTORY_GRAY]
  if (lowC != null) cases.push(['<', ['get', 'tempC'], lowC], HISTORY_BLUE)
  if (highC != null) cases.push(['>=', ['get', 'tempC'], highC], HISTORY_RED)
  cases.push(HISTORY_GREEN)
  return cases as unknown as mapboxgl.ExpressionSpecification
}

/**
 * Tracker map sources/layers: a clustered source for all trackers plus a separate
 * source for the currently-selected tracker (animated marker). Mirrors the asset
 * map's layer structure, minus geofences, colored by tracker status not severity.
 *
 * In history mode the normal layers are hidden and the selected tracker's shipment
 * location trail is drawn instead: a route line plus per-fix markers colored
 * green/red against the temperature slider threshold.
 */
export const TrackerMapLayers = () => {
  const context = useTrackerMapContext()
  const { current: map } = useMap()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'

  const { historyMode, historyPoints, historyLowC, historyHighC, historySelectedIndex } = context
  const historyCircleColor = historyColorExpression(historyLowC, historyHighC)

  // Keep the selected marker rendered from summary data until the detail loads,
  // so it doesn't blink out during the fetch.
  const currentFeature = useMemo(() => {
    if (!context.currentRowId) return undefined
    const loc = context.animatedTrackerLocation
    const row = context.filteredTrackers.find((t) => rowId(t) === context.currentRowId)
    if (!loc || !row) return undefined
    map?.triggerRepaint()
    return {
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [loc.longitude, loc.latitude] },
      properties: {
        id: rowId(row),
        label: row.shipmentName ?? row.label ?? '',
        status: getRowStatus(row),
      },
    }
  }, [context.currentRowId, context.animatedTrackerLocation, context.filteredTrackers, map])

  // Route line (LineString) through the ordered location fixes.
  const historyRouteData = useMemo<GeoJSON.Feature | undefined>(() => {
    if (!historyPoints || historyPoints.length < 2) return undefined
    return {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: historyPoints.map((p) => [p.longitude, p.latitude]) },
      properties: {},
    }
  }, [historyPoints])

  // Per-fix point features. hasTemp flags fixes with no correlated reading so the
  // paint expression can grey them out (null can't be compared numerically).
  const historyPointsData = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: (historyPoints ?? []).map((p, index) => ({
        type: 'Feature' as const,
        id: index,
        geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
        properties: {
          index,
          tempC: p.temperatureCelsius ?? 0,
          hasTemp: p.temperatureCelsius != null,
        },
      })),
    }),
    [historyPoints]
  )

  // Fit the map to the trail when a new set of history points loads.
  useEffect(() => {
    if (!historyMode || !map || !historyPoints || historyPoints.length === 0) return
    let west = Infinity
    let south = Infinity
    let east = -Infinity
    let north = -Infinity
    for (const p of historyPoints) {
      if (p.longitude < west) west = p.longitude
      if (p.longitude > east) east = p.longitude
      if (p.latitude < south) south = p.latitude
      if (p.latitude > north) north = p.latitude
    }
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 80, maxZoom: 14, duration: 800 }
    )
  }, [historyMode, historyPoints, map])

  const selectedPoint = historySelectedIndex != null && historyPoints ? historyPoints[historySelectedIndex] : undefined

  // --- History mode: only the trail, route line, and point popup ---
  if (historyMode) {
    return (
      <>
        {historyRouteData && (
          <Source id="history-route-source" type="geojson" data={historyRouteData}>
            <Layer
              id="history-route-line"
              type="line"
              layout={{ 'line-join': 'round', 'line-cap': 'round' }}
              paint={{ 'line-color': '#6b7280', 'line-width': 2, 'line-opacity': 0.6 }}
            />
          </Source>
        )}

        <Source id="history-points-source" type="geojson" data={historyPointsData}>
          <Layer
            id="history-points-circle"
            type="circle"
            paint={{
              // react-map-gl diffs paint, so dragging the slider recolors without
              // rebuilding the source data.
              'circle-color': historyCircleColor,
              'circle-radius': 6,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff',
            }}
          />
          {/* Highlight ring for the selected fix. Filter to an impossible index
              when nothing is selected so it renders nothing. */}
          <Layer
            id="history-points-selected"
            type="circle"
            filter={['==', ['get', 'index'], historySelectedIndex ?? -1]}
            paint={{
              'circle-color': historyCircleColor,
              'circle-radius': 9,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#1f2937',
            }}
          />
        </Source>

        {selectedPoint && (
          <Popup
            longitude={selectedPoint.longitude}
            latitude={selectedPoint.latitude}
            anchor="bottom"
            offset={14}
            closeOnClick={false}
            onClose={() => context.setHistorySelectedIndex(undefined)}
            maxWidth="240px"
          >
            <div className="text-xs space-y-1 py-0.5">
              <div className="font-medium">
                {tsToMillis(selectedPoint.time)
                  ? DateTime.fromMillis(tsToMillis(selectedPoint.time)!).toLocaleString(DateTime.DATETIME_MED)
                  : '—'}
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-base-content/70">Temperature</span>
                <span className="font-medium">{formatTemp(selectedPoint.temperatureCelsius, units)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-base-content/70">Location</span>
                <span>
                  {selectedPoint.latitude.toFixed(4)}, {selectedPoint.longitude.toFixed(4)}
                </span>
              </div>
            </div>
          </Popup>
        )}
      </>
    )
  }

  return (
    <>
      <Source
        key={`tracker-source-${context.mapState.style}-${context.mapState.clustersEnabled}-${context.companyId || 0}`}
        id="trackers-source"
        type="geojson"
        data={context.geojsonData}
        cluster={context.mapState.clustersEnabled}
        clusterMaxZoom={14}
        clusterRadius={50}
        clusterProperties={{ maxStatus: ['max', ['get', 'statusValue']] }}
      >
        <Layer
          id="clustered-tracker-circle"
          type="circle"
          source="trackers-source"
          filter={['has', 'point_count']}
          paint={{
            'circle-color': ['step', ['get', 'maxStatus'], '#3b82f6', 1, '#eab308'],
            'circle-radius': ['step', ['get', 'point_count'], 20, 100, 25, 750, 30],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
          }}
        />
        <Layer
          id="clustered-tracker-label"
          source="trackers-source"
          type="symbol"
          filter={['has', 'point_count']}
          layout={{
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
          }}
          paint={{ 'text-color': '#fff' }}
        />
        <Layer
          id="unclustered-tracker-marker"
          source="trackers-source"
          type="symbol"
          filter={['!', ['has', 'point_count']]}
          layout={{
            'icon-image': [
              'case',
              ['==', ['get', 'status'], 'stale'],
              statusToMarkerImage('stale'),
              ['==', ['get', 'status'], 'completed'],
              statusToMarkerImage('completed'),
              ['==', ['get', 'status'], 'warning'],
              statusToMarkerImage('warning'),
              statusToMarkerImage('ok'),
            ],
            'icon-allow-overlap': true,
          }}
          paint={{ 'icon-opacity': ['case', ['==', ['feature-state', 'selected'], true], 0.0, 1] }}
        />
        {context.mapState.trackerLabels && (
          <Layer
            id="unclustered-tracker-text"
            source="trackers-source"
            type="symbol"
            filter={['!', ['has', 'point_count']]}
            layout={{
              'text-field': ['get', 'label'],
              'text-font': ['Inter Medium'],
              'text-size': 13,
              'text-anchor': 'top',
              'text-offset': [0, 1.9],
              'icon-image': [
                'case',
                ['==', ['get', 'selected'], true],
                context.mapState.style === 'satellite' ? 'label-bg-dark-selected' : 'label-bg-light-selected',
                context.mapState.style === 'satellite' ? 'label-bg-dark' : 'label-bg-light',
              ],
              'icon-text-fit': 'both',
              'icon-allow-overlap': true,
            }}
            paint={{
              'text-color': context.mapState.style === 'satellite' ? 'white' : '#0f172a',
            }}
          />
        )}
      </Source>

      {currentFeature && (
        <Source
          key={`current-tracker-${context.currentRowId}`}
          id="current-tracker-source"
          type="geojson"
          data={currentFeature}
          cluster={false}
        >
          <Layer
            id="current-tracker-marker"
            source="current-tracker-source"
            type="symbol"
            layout={{
              'icon-image': [
                'case',
                ['any', ['==', ['get', 'status'], 'stale'], ['==', ['get', 'status'], 'completed']],
                'animated-offline-marker',
                ['==', ['get', 'status'], 'warning'],
                'animated-warning-marker',
                'animated-default-marker',
              ],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-size': 1.2,
            }}
          />
        </Source>
      )}
    </>
  )
}
